import React, { useState, useRef, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';

import { useTheme } from './hooks/useTheme';
import { useVideoAgent } from './hooks/useVideoAgent';
import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import { geminiApiService } from './services/geminiService';

import { Header } from './components/Header/Header';
import { AvatarStage } from './components/AvatarStage/AvatarStage';
import { SessionControls } from './components/Controls/SessionControls';
import { ChatPanel } from './components/Chat/ChatPanel';
import { CONNECTION_STATES, PRESET_AVATARS } from './constants';

import './App.css';

export default function App() {
  const { theme, toggleTheme } = useTheme();

  // Video element refs
  const idleVideoRef = useRef(null);
  const talkVideoRef = useRef(null);

  // Chat message & input state
  const [chat, setChat] = useState(() => {
    try {
      const saved = sessionStorage.getItem('o7_chat_history');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Video Agent Hook
  const {
    connectionStatus,
    isTalking,
    avatarUrl,
    setAvatarUrl,
    avatarPreview,
    setAvatarPreview,
    selectedVoice,
    setSelectedVoice,
    connect,
    disconnect,
    speak,
    playKeywordVideo,
  } = useVideoAgent({ idleVideoRef, talkVideoRef });

  // Send message handler
  const handleSendMessage = useCallback(async (textToSend) => {
    const query = (textToSend || inputMessage).trim();
    if (!query || isLoading) return;

    // Check for keyword video triggers (e.g. infrastructure showcase)
    if (query.toLowerCase().includes('infra')) {
      playKeywordVideo('/Recording 2025-09-13 113341.mp4');
    }

    // Add user message to conversation
    const userMsg = {
      sender: 'user',
      text: query,
      timestamp: new Date().toISOString(),
    };

    setChat((prev) => {
      const updated = [...prev, userMsg];
      sessionStorage.setItem('o7_chat_history', JSON.stringify(updated));
      return updated;
    });

    setInputMessage('');
    setIsLoading(true);

    try {
      // If not connected, start connection in parallel with Gemini request
      let aiResponse;
      if (connectionStatus === CONNECTION_STATES.DISCONNECTED || connectionStatus === CONNECTION_STATES.ERROR) {
        let currentUrl = avatarUrl;
        
        // Auto-select first persona if none is selected
        if (!currentUrl) {
          const defaultPersona = PRESET_AVATARS[0];
          setAvatarUrl(defaultPersona.url);
          setAvatarPreview(defaultPersona.preview);
          setSelectedVoice(defaultPersona.voiceId);
          currentUrl = defaultPersona.url;
          toast(`Auto-selected persona: ${defaultPersona.name}`);
        }

        const [res] = await Promise.all([
          geminiApiService.askAssistant(query),
          connect(currentUrl)
        ]);
        aiResponse = res;
      } else {
        aiResponse = await geminiApiService.askAssistant(query);
      }

      // Add assistant response to conversation
      const assistantMsg = {
        sender: 'assistant',
        text: aiResponse,
        timestamp: new Date().toISOString(),
      };

      setChat((prev) => {
        const updated = [...prev, assistantMsg];
        sessionStorage.setItem('o7_chat_history', JSON.stringify(updated));
        return updated;
      });

      // Animate video avatar or audio speech
      await speak(aiResponse);
    } catch (err) {
      console.error('[App] Message handling error:', err);
      toast.error(err.message || 'Failed to get response from assistant.');
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, playKeywordVideo, speak, avatarUrl, connectionStatus, connect, setAvatarUrl, setAvatarPreview, setSelectedVoice]);

  // Speech Recognition Hook
  const {
    isListening,
    transcript: voiceTranscript,
    toggleListening,
  } = useSpeechRecognition({
    onResult: (finalTranscript) => {
      handleSendMessage(finalTranscript);
    },
  });

  // Replay speech handler
  const handleReplaySpeech = useCallback((text) => {
    speak(text);
  }, [speak]);

  // Clear chat handler
  const handleResetChat = useCallback(() => {
    sessionStorage.removeItem('o7_chat_history');
    setChat([]);
    toast('Conversation history cleared.');
  }, []);

  // Avatar selection handler
  const handleSelectAvatar = useCallback((url, preview, voiceId) => {
    setAvatarUrl(url);
    if (preview) setAvatarPreview(preview);
    if (voiceId) setSelectedVoice(voiceId);
    
    if (connectionStatus !== CONNECTION_STATES.DISCONNECTED && connectionStatus !== CONNECTION_STATES.ERROR) {
      disconnect();
      toast('Avatar changed. Session will auto-connect on next message.');
    } else {
      toast('Avatar persona selected.');
    }
  }, [setAvatarUrl, setAvatarPreview, setSelectedVoice, connectionStatus, disconnect]);

  // Custom avatar upload handler
  const handleAvatarUploaded = useCallback((secureUrl, localPreview) => {
    setAvatarUrl(secureUrl);
    setAvatarPreview(localPreview);
    
    if (connectionStatus !== CONNECTION_STATES.DISCONNECTED && connectionStatus !== CONNECTION_STATES.ERROR) {
      disconnect();
      toast('Custom photo uploaded. Session will auto-connect on next message.');
    }
  }, [setAvatarUrl, setAvatarPreview, connectionStatus, disconnect]);

  return (
    <div className="app-root-container">
      {/* Top Application Header */}
      <Header
        connectionStatus={connectionStatus}
        selectedVoice={selectedVoice}
        onVoiceChange={setSelectedVoice}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      {/* Main Grid Layout */}
      <main className="main-content-layout">
        {/* Left Column: Avatar Stage & Controls */}
        <section className="avatar-stage-section">
          <AvatarStage
            idleVideoRef={idleVideoRef}
            talkVideoRef={talkVideoRef}
            connectionStatus={connectionStatus}
            isTalking={isTalking}
            avatarUrl={avatarUrl}
            onSelectAvatar={handleSelectAvatar}
            avatarPreview={avatarPreview}
          />

          <SessionControls
            connectionStatus={connectionStatus}
            onConnect={() => connect(avatarUrl)}
            onDisconnect={disconnect}
            onResetChat={handleResetChat}
            avatarUrl={avatarUrl}
            onAvatarUploaded={handleAvatarUploaded}
          />
        </section>

        {/* Right Column: Chat History & Input Omnibar */}
        <section className="chat-section">
          <ChatPanel
            chat={chat}
            isLoading={isLoading}
            onSendMessage={handleSendMessage}
            onReplaySpeech={handleReplaySpeech}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            isListening={isListening}
            onToggleVoice={toggleListening}
            voiceTranscript={voiceTranscript}
          />
        </section>
      </main>

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#181A22',
            color: '#F2F3F8',
            border: '1px solid rgba(255,255,255,0.09)',
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif'
          },
          success: {
            iconTheme: {
              primary: '#33D690',
              secondary: '#181A22',
            },
          },
        }}
      />
    </div>
  );
}